import { ClassList } from 'lib/utils/react/classlist';
import React, { useMemo } from 'react';
import {
  LuCheckCircle,
  LuChevronDown,
  LuChevronUp,
  LuCircle,
  LuPlusCircle
} from 'react-icons/lu';

export interface HierachyItem {
  label: string;
  level: number;
  isSelectable: boolean;
  hasChildren(): boolean;
  children(): HierachyItem[];
}

/**
 * Props for the SelectMultiHierarchy component.
 */
export type SelectMultiHierarchyProps<T> = {
  model: { valuesHierarchy: () => T[] };
  maxLevels?: number;
  exclude?: T[];
  selected: T[];
  toKey: (item: T) => string;
  fromKey: (key: string) => T;
  onChange: (selected: T[]) => void;
};

/**
 * A multiple-select component that allows for hierarchical selection.
 *
 * @param props React props
 */
export const SelectMultiHierarchy = <T extends HierachyItem>({
  model,
  maxLevels = 99,
  exclude,
  selected,
  toKey,
  fromKey,
  onChange
}: SelectMultiHierarchyProps<T>) => {
  // Exclude items lookup
  const excludeSet = useMemo(() => {
    const excludeSet = new Set<string>();
    exclude?.forEach((item) => excludeSet.add(toKey(item)));
    return excludeSet;
  }, [(model as any).name, exclude?.length ?? 0]);

  // Selected map
  const selectedMap = new Map<string, T>();
  selected.forEach((item) => selectedMap.set(toKey(item), item));

  // Limit depth
  let levelsRemaining = maxLevels - 1;

  // Render the component
  return (
    <div className="multi-select-2 is-large">
      {model
        .valuesHierarchy()
        .filter((item) => !excludeSet.has(toKey(item)))
        .map((item: T) => (
          <HierachyItem<T>
            key={item.label}
            excludeSet={excludeSet}
            selected={selected}
            selectedMap={selectedMap}
            item={item}
            levelsRemaining={levelsRemaining}
            toKey={toKey}
            fromKey={fromKey}
            onChange={onChange}
          />
        ))}
    </div>
  );
};

/**
 * (Internal usage only.) The props for the HierachyItem component.
 */
type HierachyItemProps<T extends HierachyItem> = {
  excludeSet: Set<string>;
  selected: T[];
  selectedMap: Map<string, T>;
  item: T;
  isParentSelected?: boolean;
  levelsRemaining: number;
  toKey: (item: T) => string;
  fromKey: (key: string) => T;
  onChange: (selected: T[]) => void;
};

/**
 * Helper method to check whether any of the descendants in a hierarchy
 * item are selected.
 *
 * This method is recursive and will check all descendants.
 *
 * @param item The current item to check
 * @param selectedMap The map of selected items
 * @param toKey The function to convert an item to a key
 * @returns Whether any of the descendants are selected
 */
const hasSelectedChildren = <T extends HierachyItem>(
  item: T,
  selectedMap: Map<string, T>,
  toKey: (item: T) => string
): boolean => {
  if (selectedMap.has(toKey(item))) {
    return true;
  }
  return item
    .children()
    .some((child) => hasSelectedChildren(child as T, selectedMap, toKey));
};

/**
 * Helper method to get the number of selected children in a hierarchy item.
 *
 * This method is recursive and will check all descendants.
 *
 * @param item The current item to check
 * @param selectedMap The map of selected items
 * @param toKey The function to convert an item to a key
 * @returns The number of selected children
 */
const getNumberOfSelectedChildren = <T extends HierachyItem>(
  item: T,
  selectedMap: Map<string, T>,
  toKey: (item: T) => string
): number => {
  const base = selectedMap.has(toKey(item)) ? 1 : 0;
  return item
    .children()
    .reduce(
      (acc, child) => acc + getNumberOfSelectedChildren(child as T, selectedMap, toKey),
      base
    );
};

/**
 * (Internal usage only.)
 *
 * A react component that renders a single item in the hierarchy.
 */
const HierachyItem = <T extends HierachyItem>({
  excludeSet,
  selected,
  selectedMap,
  item,
  isParentSelected,
  levelsRemaining,
  toKey,
  fromKey,
  onChange
}: HierachyItemProps<T>) => {
  // Check for children
  const hasChildren = levelsRemaining > 0 ? item.hasChildren() : false;
  const children = levelsRemaining > 0 ? item.children() : [];
  const isChildrenSelected =
    hasChildren &&
    children.some((child) => hasSelectedChildren(child as T, selectedMap, toKey));

  // Whether this item is selected
  const isSelected = selectedMap.has(toKey(item));

  // Whether the parent is selected
  const selectedChildren = isChildrenSelected
    ? getNumberOfSelectedChildren(item, selectedMap, toKey)
    : 0;

  // Whether the item is expanded
  const [isOpen, setIsOpen] = React.useState(isChildrenSelected);

  // Classes for the item
  const groupClasses = ClassList.of('select-item-group')
    .add('selected', isSelected)
    .add('selected-parent', isParentSelected);

  const itemClasses = ClassList.of('select-item')
    .add('selected', isSelected)
    .add('selected-children', isChildrenSelected);

  // Render the component
  return (
    <div className={groupClasses.names}>
      <div
        className={itemClasses.names}
        onClick={(e) => {
          if (!item.isSelectable) {
            return;
          }

          if (isSelected) {
            const newSelected = [...selected];
            newSelected.splice(newSelected.indexOf(item), 1);
            onChange(newSelected);
          } else {
            onChange([...selected, item]);
          }
        }}
      >
        {item.isSelectable ? (
          isSelected ? (
            <LuCheckCircle />
          ) : (
            <LuPlusCircle />
          )
        ) : (
          <LuCircle />
        )}
        <span className={isSelected ? 'font-medium' : ''}>
          {item.label}
          {selectedChildren > 0 ? (
            <span className="font-normal text-subtle"> ({selectedChildren})</span>
          ) : null}
        </span>
        {hasChildren &&
          (isOpen ? (
            <LuChevronUp
              className="pull-right"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
            />
          ) : (
            <LuChevronDown
              className="pull-right"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
            />
          ))}
      </div>
      <div className="select-item-children">
        {isOpen &&
          children
            .filter((item) => !excludeSet.has(toKey(item as T)))
            .map((child) => (
              <HierachyItem<T>
                key={child.label}
                excludeSet={excludeSet}
                selected={selected}
                selectedMap={selectedMap}
                item={child as T}
                levelsRemaining={levelsRemaining - 1}
                toKey={toKey}
                fromKey={fromKey}
                onChange={onChange}
              />
            ))}
      </div>
    </div>
  );
};
